/**
 * WK Header — mega hover, category product switch, mobile drawer panels
 */
(function () {
  const CLOSE_DELAY = 180;
  const MQ_DESKTOP = window.matchMedia('(min-width: 1000px)');

  class WkHeader {
    constructor(root) {
      this.root = root;
      this.stickyMode = root.dataset.sticky || 'none';
      this.transparent = root.dataset.transparent === 'true';
      this.closeTimers = new WeakMap();
      this.drawer = root.querySelector('[data-wk-nav-drawer]');
      this._stickyOn = false;
      this._solidOn = false;
      this._scrollRaf = 0;
      this._activePanelId = null;
      this.bindSticky();
      if (MQ_DESKTOP.matches) this.bindMega();
      this.bindDrawer();
      this.bindEsc();
    }

    bindSticky() {
      if (this.stickyMode === 'none' && !this.transparent) return;

      const apply = () => {
        this._scrollRaf = 0;
        const y = window.scrollY || window.pageYOffset;

        if (this.stickyMode === 'always') {
          const next = y > 4;
          if (next !== this._stickyOn) {
            this._stickyOn = next;
            this.root.classList.toggle('is-sticky', next);
          }
        }

        if (this.transparent) {
          const next = y > 16;
          if (next !== this._solidOn) {
            this._solidOn = next;
            this.root.classList.toggle('is-solid', next);
          }
        }
      };

      const onScroll = () => {
        if (this._scrollRaf) return;
        this._scrollRaf = requestAnimationFrame(apply);
      };

      apply();
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    bindMega() {
      const items = this.root.querySelectorAll('[data-wk-nav-item]');
      items.forEach((item) => {
        const details = item.querySelector('[data-wk-mega-details]');
        if (!details) return;

        const open = () => {
          clearTimeout(this.closeTimers.get(details));
          items.forEach((other) => {
            const d = other.querySelector('[data-wk-mega-details]');
            if (d && d !== details) d.open = false;
          });
          details.open = true;
        };

        const scheduleClose = () => {
          clearTimeout(this.closeTimers.get(details));
          const t = setTimeout(() => {
            details.open = false;
          }, CLOSE_DELAY);
          this.closeTimers.set(details, t);
        };

        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', scheduleClose);
        details.addEventListener('focusin', open);
        details.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) scheduleClose();
        });

        const summary = details.querySelector('summary');
        if (summary) {
          summary.addEventListener('click', (e) => {
            if (!MQ_DESKTOP.matches) return;
            e.preventDefault();
            const url = details.getAttribute('data-url');
            if ((e.metaKey || e.ctrlKey) && url && url !== '#') {
              window.open(url, '_blank');
            }
          });
        }

        const mega = details.querySelector('[data-wk-mega="products"]');
        if (mega) this.bindProductSwitch(mega);
      });
    }

    bindProductSwitch(mega) {
      const links = mega.querySelectorAll('[data-wk-mega-index]');
      const panels = mega.querySelectorAll('[data-wk-mega-panel]');
      if (!links.length || !panels.length) return;

      const activate = (index) => {
        const key = String(index);
        links.forEach((link) => {
          link.classList.toggle('is-active', link.getAttribute('data-wk-mega-index') === key);
        });
        panels.forEach((panel) => {
          const match = panel.getAttribute('data-wk-mega-panel') === key;
          panel.classList.toggle('is-active', match);
          panel.toggleAttribute('hidden', !match);
        });
      };

      links.forEach((link) => {
        const go = () => activate(link.getAttribute('data-wk-mega-index'));
        link.addEventListener('mouseenter', go);
        link.addEventListener('focus', go);
      });
    }

    bindDrawer() {
      if (!this.drawer) return;

      const openBtns = this.root.querySelectorAll('[data-wk-drawer-open]');
      const closeEls = this.drawer.querySelectorAll('[data-wk-drawer-close]');
      const panelEl = this.drawer.querySelector('.wk-nav-drawer__panel');
      let closeTimer = null;

      const setOpen = (open) => {
        clearTimeout(closeTimer);
        if (open) {
          if (this.drawer.classList.contains('is-open') && !this.drawer.hidden) return;

          this.drawer.hidden = false;
          this.drawer.setAttribute('aria-hidden', 'false');
          document.body.classList.add('wk-nav-open');
          openBtns.forEach((b) => b.setAttribute('aria-expanded', 'true'));
          this.gotoPanel('0');

          // Wait one frame so closed state paints, then open — avoids layout glitch
          requestAnimationFrame(() => {
            this.drawer.classList.add('is-open');
          });
        } else {
          if (this.drawer.hidden && !this.drawer.classList.contains('is-open')) return;

          this.drawer.classList.remove('is-open');
          this.drawer.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('wk-nav-open');
          openBtns.forEach((b) => b.setAttribute('aria-expanded', 'false'));

          const finish = () => {
            if (!this.drawer.classList.contains('is-open')) {
              this.drawer.hidden = true;
            }
          };

          if (panelEl) {
            const onEnd = (e) => {
              if (e.target !== panelEl || e.propertyName !== 'transform') return;
              panelEl.removeEventListener('transitionend', onEnd);
              finish();
            };
            panelEl.addEventListener('transitionend', onEnd);
          }
          closeTimer = setTimeout(finish, 160);
        }
      };

      this.openDrawer = () => setOpen(true);
      this.closeDrawer = () => setOpen(false);

      openBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          setOpen(true);
        });
      });

      closeEls.forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          setOpen(false);
        });
      });

      // Event delegation via click only
      this.drawer.addEventListener('click', (e) => {
        const gotoBtn = e.target.closest('[data-wk-panel-goto]');
        if (gotoBtn && this.drawer.contains(gotoBtn)) {
          e.preventDefault();
          this.gotoPanel(gotoBtn.getAttribute('data-wk-panel-goto'));
          return;
        }

        const rootToggle = e.target.closest('[data-wk-root-toggle]');
        if (rootToggle && this.drawer.contains(rootToggle)) {
          e.preventDefault();
          this.toggleRootItem(rootToggle.closest('[data-wk-root-item]'));
          return;
        }

        const accToggle = e.target.closest('[data-wk-acc-toggle]');
        if (accToggle && this.drawer.contains(accToggle)) {
          e.preventDefault();
          this.toggleAccItem(accToggle.closest('[data-wk-acc-item]'));
        }
      });

      document.addEventListener('click', (e) => {
        if (!this.drawer || this.drawer.hidden) return;
        const cartTrigger = e.target.closest(
          '[data-open-cart-drawer], .m-cart-icon-bubble, [data-open-search-popup]'
        );
        if (cartTrigger) setOpen(false);
      });
    }

    toggleRootItem(item) {
      if (!item || !this.drawer) return;
      const list = this.drawer.querySelector('[data-wk-root-list]');
      if (!list) return;
      const opening = !item.classList.contains('is-open');

      list.querySelectorAll('.wk-root-item.is-open').forEach((other) => {
        if (other === item && opening) return;
        other.classList.remove('is-open');
        const btn = other.querySelector('[data-wk-root-toggle]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });

      item.classList.toggle('is-open', opening);
      const btn = item.querySelector('[data-wk-root-toggle]');
      if (btn) btn.setAttribute('aria-expanded', opening ? 'true' : 'false');
    }

    toggleAccItem(item) {
      if (!item) return;
      const acc = item.closest('[data-wk-accordion]');
      if (!acc) return;
      const opening = !item.classList.contains('is-open');

      acc.querySelectorAll('.wk-acc-item.is-open').forEach((other) => {
        if (other === item && opening) return;
        other.classList.remove('is-open');
        const otherBtn = other.querySelector('[data-wk-acc-toggle]');
        if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
      });

      item.classList.toggle('is-open', opening);
      const btn = item.querySelector('[data-wk-acc-toggle]');
      if (btn) btn.setAttribute('aria-expanded', opening ? 'true' : 'false');
    }

    gotoPanel(id) {
      if (!this.drawer) return;
      const targetId = String(id);
      if (this._activePanelId === targetId) return;
      this._activePanelId = targetId;

      const panels = this.drawer.querySelectorAll('[data-wk-drawer-panel]');
      let activeScroller = null;

      panels.forEach((panel) => {
        const match = panel.getAttribute('data-wk-drawer-panel') === targetId;
        const isRoot = panel.getAttribute('data-level') === '0';
        panel.classList.toggle('is-active', match);

        if (match) {
          panel.removeAttribute('hidden');
          panel.setAttribute('data-wk-visited', '');
          activeScroller = panel.querySelector('.wk-drawer-panel__scroller');
        } else if (isRoot) {
          panel.removeAttribute('hidden');
        } else if (!panel.hasAttribute('data-wk-visited')) {
          panel.setAttribute('hidden', '');
        }
      });

      if (activeScroller) activeScroller.scrollTop = 0;
    }

    bindEsc() {
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (MQ_DESKTOP.matches) {
          this.root.querySelectorAll('[data-wk-mega-details]').forEach((d) => {
            d.open = false;
          });
        }
        if (this.closeDrawer) this.closeDrawer();
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-wk-header]').forEach((el) => {
      if (el._wkHeader) return;
      el._wkHeader = new WkHeader(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-wk-header]');
    if (root && !root._wkHeader) root._wkHeader = new WkHeader(root);
  });
})();
